import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    hardLimit: { type: Number, default: 4000 },
    maxAnnualHours: Number,
  };

  static targets = [
    "menInput",
    "womenInput",
    "menErrorContainer",
    "womenErrorContainer",
    "menWarningContainer",
    "womenWarningContainer",
  ];

  connect() {
    this.entryForm = this.element.closest('[data-payroll-entry]');
    this.debouncedValidate = this.#debounce(this.validate.bind(this), 200);
    
    // Listen for server-side validation errors
    this.element.addEventListener("totalHoursValidation:serverError", this._handleServerError.bind(this));
  }

  disconnect() {
    this.element.removeEventListener("totalHoursValidation:serverError", this._handleServerError);
  }

  validateMen() {
    if (this.hasMenInputTarget) {
      const value = parseFloat(this.menInputTarget.value) || 0;
      this._checkHardLimit(value, "men");
      this._checkMaxAnnualHours(value, "men");
    }
  }

  validateWomen() {
    if (this.hasWomenInputTarget) {
      const value = parseFloat(this.womenInputTarget.value) || 0;
      this._checkHardLimit(value, "women");
      this._checkMaxAnnualHours(value, "women");
    }
  }

  validate() {
    this.validateMen();
    this.validateWomen();
  }

  _checkHardLimit(value, gender) {
    const isMen = gender === "men";
    const input = isMen ? this.menInputTarget : this.womenInputTarget;
    const errorContainer = isMen ? this.menErrorContainerTarget : this.womenErrorContainerTarget;

    if (value > this.hardLimitValue) {
      this._displayError(
        errorContainer,
        input,
        this.element.dataset.errorMessage ||
        "Ordinary hours worked cannot exceed 4000 hours per year"
      );
      this._markEntryIncomplete();
    } else {
      this._clearErrors(errorContainer, input);
    }
  }

  _checkMaxAnnualHours(value, gender) {
    if (!this.hasMaxAnnualHoursValue || value <= this.maxAnnualHoursValue) {
      const isMen = gender === "men";
      const warningContainer = isMen ? this.menWarningContainerTarget : this.womenWarningContainerTarget;
      this._clearWarnings(warningContainer);
      return;
    }

    const isMen = gender === "men";
    const warningContainer = isMen ? this.menWarningContainerTarget : this.womenWarningContainerTarget;
    this._displayWarning(
      warningContainer,
      this.element.dataset.warningMessage ||
      "Hours exceed the maximum annual hours limit for this country"
    );
  }

  _displayError(container, input, message) {
    if (container) {
      const span = container.querySelector("span");
      if (span) span.textContent = message;
      container.classList.remove("hidden");
    }
    
    if (input) {
      input.classList.add("border-red-500");
    }
  }

  _displayWarning(container, message) {
    if (container) {
      const span = container.querySelector("span");
      if (span) span.textContent = message;
      container.classList.remove("hidden");
    }
  }

  _clearErrors(container, input) {
    if (container) {
      container.classList.add("hidden");
    }
    
    if (input) {
      input.classList.remove("border-red-500");
    }
  }

  _clearWarnings(container) {
    if (container) {
      container.classList.add("hidden");
    }
  }

  _markEntryIncomplete() {
    if (this.entryForm) {
      this.entryForm.setAttribute("data-payroll-entry-incomplete", "true");
    }
  }

  _handleServerError(event) {
    const { field, message } = event.detail;
    
    // Check if error is for men or women field
    if (field.includes("men_payroll_datum.total_hours") && this.hasMenInputTarget) {
      this._displayError(
        this.menErrorContainerTarget,
        this.menInputTarget,
        message
      );
      this._markEntryIncomplete();
    } else if (field.includes("women_payroll_datum.total_hours") && this.hasWomenInputTarget) {
      this._displayError(
        this.womenErrorContainerTarget,
        this.womenInputTarget,
        message
      );
      this._markEntryIncomplete();
    }
  }

  #debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  }
}


